import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { ArrowRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface DealHistoryProps {
  dealId: string;
}

export function DealHistory({ dealId }: DealHistoryProps) {
  const [history, setHistory] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    fetchHistory();
  }, [dealId]);

  const fetchHistory = async () => {
    try {
      const { data, error } = await supabase
        .from("deal_stage_history")
        .select(`
          *,
          from_stage:pipeline_stages!deal_stage_history_from_stage_id_fkey(name, color),
          to_stage:pipeline_stages!deal_stage_history_to_stage_id_fkey(name, color)
        `)
        .eq("deal_id", dealId)
        .order("changed_at", { ascending: false });

      if (error) throw error;
      setHistory(data || []);
    } catch (error) {
      console.error("Error fetching history:", error);
      toast({
        title: "Error",
        description: "Failed to load stage history",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading history...</div>;
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">Stage Change History</h3>

      {history.length === 0 ? (
        <Card className="p-8 text-center text-muted-foreground">
          No stage changes recorded yet. The deal history will appear here when you move the deal between stages.
        </Card>
      ) : (
        <div className="space-y-3">
          {history.map((entry) => (
            <Card key={entry.id} className="p-4">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 flex-1">
                  {entry.from_stage && (
                    <>
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full flex-shrink-0"
                          style={{ backgroundColor: entry.from_stage.color || "#999" }}
                        />
                        <span className="font-medium">{entry.from_stage.name}</span>
                      </div>
                      <ArrowRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    </>
                  )}
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: entry.to_stage?.color || "#999" }}
                    />
                    <span className="font-medium">{entry.to_stage?.name}</span>
                  </div>
                </div>
                <span className="text-sm text-muted-foreground whitespace-nowrap">
                  {new Date(entry.changed_at).toLocaleString()}
                </span>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
